import React, { useContext, useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { useRouter } from 'next/router'

import styled from 'styled-components'

import { StoreContext } from 'contexts/StoreContext'
import { filterMenu } from 'utils/api/loadMenu/loadMenu'
import { getLocaleStr, codeToLocale } from 'utils/utils'
import { getCurrentDateStringByTimezone } from 'utils/timeUtils'
import { CONSTANTS, Page } from 'utils/constants'
import UnrecoverableServerError from '../../../components/errors/unrecoverableServerError'
import MenuGrid from '../../components/menu/MenuGrid'
import CustomHead from '../../../components/CustomHead'
import Skeleton from 'components/Skeleton'

import type { LanguageCode } from 'utils/utils'
import { useStores } from 'hooks/useStores'

const StaticMenuContainer = styled.div`
	.MuiTabs-scroller.MuiTabs-scrollable{
		display: flex;

		.MuiTabs-flexContainer {
			margin: 0 auto;
		}
	}

	.MuiGrid-root.MuiGrid-container {
		max-width: 1280px;
		margin: 0 auto;
	}
`

const IS_STATIC_MENU = true

const Menu = inject(
	'Infra',
	'Application',
	'Home',
	'User'
)(
	observer(
		({
			Application,
			Infra,
			Home,
			User,
			data,
			lang,
			traceId,
			error,
		}: {
			Application: any
			Infra: any
			Home: any
			User: any
			data: any
			lang: LanguageCode
			traceId: string
			error: any
		}) => {
			// Hydrate the StoreContext with the menu data (fetched on server side)
			const { store, setStore } = useContext(StoreContext)
			const router = useRouter()
			const title = getLocaleStr(Infra.appParams?.pageText?.menu?.title, codeToLocale[lang] || 'en_US')
			const description = getLocaleStr(Infra.appParams?.pageText?.menu?.description, codeToLocale[lang] || 'en_US')
			const [filterApplied, setFilterApplied] = useState(false)
			const { cartStore, addressManagerV2Store } = useStores()

			// TODO FOR NEXT: check if after migration to app directory, we can use setStore here
			if (!filterApplied) {
				store.data = data
			}

			useEffect(() => {
				const newData = filterMenu({
					menu: data,
					orderType: 'all',
					channelType: Application.isMobileApp ? CONSTANTS.CHANNEL_TYPE.APP : CONSTANTS.CHANNEL_TYPE.WEB,
					date: getCurrentDateStringByTimezone(data.timezone),
				})

				setStore((prevStore) => ({
					...prevStore,
					data: newData,
				}))
				setFilterApplied(true)

				const redirectToChainMenu = async () => {
					const { menuRes, storeMetaData, serverSession, menuUrl } = await addressManagerV2Store.onMenuClickWhenLocalized(cartStore, User)

					await User.setSession(serverSession)

					setStore((prevStore: any) => ({ ...prevStore, data: menuRes, metaData: storeMetaData }))
					router.push(menuUrl)
				}

				if(addressManagerV2Store.newLocalizationFlowEnabled && addressManagerV2Store.alreadyLocalized && !Home.areAllStoresClosed()) {
					addressManagerV2Store.goToLocalizedMenu()
				}

				if (!addressManagerV2Store.newLocalizationFlowEnabled && addressManagerV2Store.isUserLocalized() && !Home.areAllStoresClosed()) {
					redirectToChainMenu()
				}
			}, [])

			if (!data) {
				return <UnrecoverableServerError message={error || 'Fail to fetch static menu page data'} traceId={traceId} />
			}

			// Remove 'ref' from appParams because it throws an error on CustomHead (string ref prop not allowed on function component, see https://legacy.reactjs.org/docs/refs-and-the-dom.html#legacy-api-string-refs)
			const { ref, ...appParamsWithoutRef } = Infra.appParams

			return (
				<>
					<CustomHead {...appParamsWithoutRef} title={title} description={description} lang={lang} key={1} />
					<Skeleton page={Page.STATIC_MENU} showMenuTabs showLogo showCustomTitle showHeader>
						<StaticMenuContainer>
							<MenuGrid isStaticMenu={IS_STATIC_MENU} isGeneratedStatically />
						</StaticMenuContainer>
					</Skeleton>
				</>
			)
		}
	)
)

export default Menu
